import RemoveIcon from "@mui/icons-material/Remove";
import Tooltip from "@mui/material/Tooltip";
import Typography, { type TypographyProps } from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { useRef, useState, type ReactNode } from "react";

type FeeScheduleTableCellTextProps = {
  children: ReactNode;
  color?: TypographyProps["color"];
  emphasized?: boolean;
  title?: string;
};

/** Shared visual treatment for text rendered in fee-schedule tables. */
export function FeeScheduleTableCellText(props: Readonly<FeeScheduleTableCellTextProps>) {
  const { children, color, title } = props;
  const emphasized = props.emphasized ?? false;
  const tooltip = title ?? (typeof children === "string" || typeof children === "number" ? String(children) : "");
  const textRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const updateTooltip = () => {
    const text = textRef.current;
    setTooltipOpen(Boolean(
      tooltip && text && (text.scrollWidth > text.clientWidth || text.scrollHeight > text.clientHeight)
    ));
  };

  return (
    <Tooltip
      open={tooltipOpen}
      title={tooltip}
      onClose={() => setTooltipOpen(false)}
      disableFocusListener
      disableTouchListener
    >
      <Typography
        ref={textRef}
        color={color}
        component="span"
        variant="body2"
        onMouseEnter={updateTooltip}
        onMouseLeave={() => setTooltipOpen(false)}
        sx={{
          display: "block",
          fontWeight: emphasized ? 500 : 400,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  );
}

/**
 * The dash a fee schedule cell shows where it has no value.
 *
 * An SVG icon rather than `<Icon>remove</Icon>`. That form renders a Material Icons *ligature* — the
 * literal text "remove", which the browser draws as a dash only once the icon font fetched from
 * Google Fonts has applied. `MuiIcon-root` is `width: 1em; overflow: hidden`, so anywhere that font
 * has not applied — an environment with no route to the CDN, a blocked request, or simply the moment
 * before it loads — the cell shows the first 1em of the word instead. That is what the Per column's
 * "re" was on a fixed-fee row, where the unit is legitimately absent: not a formatting quirk but the
 * placeholder failing open into its own source text.
 *
 * An SVG icon is bundled with the app and cannot fail that way.
 */
export function FeeScheduleTableCellNoValue() {
  return <RemoveIcon fontSize="small" />;
}

type FeeScheduleTableCellStyleOptions = {
  columnId?: string;
  truncate?: boolean;
};

/** Keeps plain values and nested MUI/redline inputs on the same body2 scale. */
export function feeScheduleTableCellStyles(
  theme: Theme,
  { columnId, truncate = true }: FeeScheduleTableCellStyleOptions = {},
) {
  if (columnId === "mrt-row-actions" || columnId === "locked") return {};

  const truncation = truncate
    ? {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }
    : {};

  return {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    minWidth: 0,
    ...truncation,
    "& .MuiTypography-root": {
      fontSize: "inherit",
      lineHeight: "inherit",
      minWidth: 0,
      ...truncation,
    },
    // MUI InputBase sets its own 1rem font size. Override the root as well as
    // the custom input so redline cells stay on the same body2 scale as plain cells.
    "& .MuiInputBase-root, & .MuiInputBase-input": {
      fontSize: "inherit",
      lineHeight: "inherit",
    },
    ...(truncate && {
      "& .MuiInputBase-input > div": {
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    }),
  };
}
